import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';

export const REQUEST_TIMEOUT_CODE = 'request/timeout';

export class RequestTimeoutError extends Error {
  readonly code = REQUEST_TIMEOUT_CODE;

  readonly alertHandled = true;

  constructor(
    readonly source: string,
    timeoutMs?: number,
  ) {
    super(timeoutMs != null ? `${source} timeout after ${timeoutMs}ms` : `${source} timeout`);
    this.name = 'RequestTimeoutError';
  }
}

let requestTimeoutAlertVisible = false;

export function isRequestTimeoutAlertShowing(): boolean {
  return requestTimeoutAlertVisible;
}

export function showRequestTimeoutAlert(): void {
  if (requestTimeoutAlertVisible) {
    return;
  }

  requestTimeoutAlertVisible = true;

  const onDismiss = () => {
    requestTimeoutAlertVisible = false;
  };

  showAlert({
    title: STRINGS.errors.requestTimeoutTitle,
    message: STRINGS.errors.requestTimeoutMessage,
    buttons: [{ text: STRINGS.errors.ok, onPress: onDismiss }],
  });
}

export function isAlertAlreadyHandled(error: unknown): boolean {
  return !!(error as { alertHandled?: boolean } | null)?.alertHandled;
}

export function isAxiosTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const { code, message } = error as { code?: string; message?: string };
  return code === 'ECONNABORTED' || (typeof message === 'string' && /timeout/i.test(message));
}

/** @deprecated Prefer `shouldSuppressErrorAlert` — kept for existing Firestore call sites. */
export function isFirestoreTimeoutError(error: unknown): boolean {
  return (
    (error instanceof RequestTimeoutError && error.source === 'Firestore') ||
    (error instanceof Error && error.message.startsWith('Firestore timeout after'))
  );
}

export function isRequestTimeoutError(error: unknown): boolean {
  if (isAlertAlreadyHandled(error)) {
    return true;
  }
  if (error instanceof RequestTimeoutError) {
    return true;
  }
  if (isFirestoreTimeoutError(error)) {
    return true;
  }
  if (error instanceof Error && error.name === 'AuthTimeoutError') {
    return true;
  }
  if ((error as { code?: string })?.code === 'auth/timeout') {
    return true;
  }
  return isAxiosTimeoutError(error);
}

/** Skip downstream generic error alerts when timeout alert was already shown. */
export function shouldSuppressErrorAlert(error: unknown): boolean {
  return isRequestTimeoutError(error);
}
